/**
 * Platform marketing shell — route subtree mounted on
 * ``forgmatic.com`` (and the legacy ``.cz`` alias). Exports the
 * children array consumed by ``router.tsx`` when
 * ``resolveShell()==='platform'``.
 *
 * No buyer flow, no per-tenant admin: this is the SaaS pitch +
 * sign-up surface only. Tenant subdomains see a different tree
 * (``TenantServiceShell``); admin.forgmatic.com sees a third
 * (``PlatformAdminShell``).
 */

import type { RouteObject } from "react-router-dom";
import { Navigate, useParams } from "react-router-dom";

import { DEFAULT_LANG } from "@/i18n";

import {
  Account,
  AccountEmailChangeConfirm,
  AuthVerifyEmail,
  Contact,
  DeveloperApps,
  FeaturesHub,
  FeaturesB2b,
  FeaturesIntegrations,
  FeaturesInvoicing,
  FeaturesProduction,
  FeaturesQuoting,
  FeaturesWarehouse,
  ForBusiness,
  InvitationsAccept,
  Lazy,
  LegalPage,
  NotFound,
  OrgsVerify,
  PasswordResetConfirm,
  SsoLinkConfirm,
  PasswordResetRequest,
  TwoFactorBootstrap,
  Changelog,
  ChangelogPost,
  PlatformHowItWorks,
  PlatformLanding,
  PlatformTryout,
  PricingPlans,
  Security,
  SignIn,
  SignUpShop,
  VsMarketplaces,
  SignUpTeam,
  SignUpTeamFinalize,
  Subprocessors,
} from "./lazyPages";
import { LegacySignInRedirect } from "./LegacySignInRedirect";

/**
 * Send a retired marketing path to the landing, keeping the language.
 *
 * ``replace`` so the dead path does not sit in history behind the
 * page the visitor actually wanted.
 */
function RedirectToLanding(): JSX.Element {
  const { lang } = useParams<{ lang: string }>();
  return <Navigate to={`/${lang ?? DEFAULT_LANG}`} replace />;
}

// Marketing-only routes for the ``platform`` shell. Buyer flow,
// per-tenant admin, and tenant-specific pages are intentionally
// absent — visiting ``forgmatic.com/cart`` is a 404 by design (the
// cart lives on the tenant subdomain). Sign-in stays on the platform
// shell so users land on a stable, non-tenant URL when authenticating,
// and ``COOKIE_DOMAIN`` carries the resulting session across to
// ``demo.forgmatic.com`` and any tenant subdomain. Generic ``/sign-up``
// was removed: signup happens via role-specific entry points
// (``/sign-up-shop`` for providers, ``/sign-up-team`` for B2B teams).
//
// Onboarding wizards (provider + customer-org) live on the tenant
// shell only — they query admin endpoints (business-info, branding,
// capabilities, kyc, customer-orgs) that resolve via the tenant
// subdomain. Mounted on platform shell they would query the wrong
// tenant ("default" via TenantContextMiddleware fallback). OrgsVerify
// + SignUpTeamFinalize cross-host into the freshly-provisioned
// tenant subdomain after the BE assigns the slug.
export const platformChildren: RouteObject[] = [
  { index: true, element: <PlatformLanding /> },
  // KAN-908 — ``/for-providers`` addressed the same shop owner as the
  // landing, in a different voice. Merged there; the path stays as a
  // redirect because it is linked from outside the app. The 301 that
  // a crawler needs is in ``vercel.json``; this covers in-app links
  // and every environment where that file does not apply (dev,
  // preview server, the prerender capture).
  { path: "for-providers", element: <RedirectToLanding /> },
  { path: "for-business", element: <Lazy><ForBusiness /></Lazy> },
  { path: "pricing-plans", element: <Lazy><PricingPlans /></Lazy> },
  // KAN-717 — NOT the tenant ``HowItWorks``. That page addresses the
  // person ordering a part ("send us your files"), which on the apex
  // made Forgmatic read as the manufacturer instead of the software it
  // sells. The platform version tells the same journey from the shop
  // owner's side. The route stays because an owner evaluating the
  // platform does want to see what their customer will walk through —
  // it is section 2 of that page.
  { path: "how-it-works", element: <Lazy><PlatformHowItWorks /></Lazy> },
  { path: "contact", element: <Lazy><Contact /></Lazy> },
  // KAN-1462 — sekce „Co umíme". Vznikla proto, že web prodával
  // nacenění na hloubku celého webu a druhou polovinu produktu větou
  // „a další — sklad, šarže, štítky" na landingu. Rozcestník je jediné
  // místo, kde je vidět šířka; jednotlivé stránky ji pak dokládají.
  //
  // Slugy jsou české schválně (``/features/sklad-a-nakup``), i když
  // starší marketingové routy anglické jsou: čtenář těchhle stránek je
  // majitel české dílny a adresa je první věta, kterou o stránce vidí.
  // Rozcestník zůstává ``/features`` — je to zadaná adresa sekce.
  { path: "features", element: <Lazy><FeaturesHub /></Lazy> },
  { path: "features/naceneni-a-objednavka", element: <Lazy><FeaturesQuoting /></Lazy> },
  { path: "features/sklad-a-nakup", element: <Lazy><FeaturesWarehouse /></Lazy> },
  { path: "features/vyroba-a-dilna", element: <Lazy><FeaturesProduction /></Lazy> },
  { path: "features/firemni-zakaznici", element: <Lazy><FeaturesB2b /></Lazy> },
  { path: "features/faktury-a-dane", element: <Lazy><FeaturesInvoicing /></Lazy> },
  { path: "features/napojeni-a-rozsireni", element: <Lazy><FeaturesIntegrations /></Lazy> },
  // KAN-1474 — the start-up wizard's first three steps with no tenant behind
  // them, over the public catalog. Where the hero's second button leads.
  { path: "try", element: <Lazy><PlatformTryout /></Lazy> },
  // Platformní stránka, ne tenantí: odpovídá majiteli dílny na
  // „kde mám data" a „co když vám to spadne". Tvrzení se čtou za
  // běhu z toho, co je rozhoduje — nic z nich není v copy.
  { path: "security", element: <Lazy><Security /></Lazy> },
  // Srovnání dvou obchodních modelů, ne dvou firem: žádné tržiště
  // se tu nejmenuje, protože doložená čísla o žádném nemáme.
  { path: "vs-marketplaces", element: <Lazy><VsMarketplaces /></Lazy> },
  // Novinky platformy. Jen apex — tenant shopy changelog platformy
  // nezobrazují; prozradil by, že obchod běží na cizí platformě.
  { path: "changelog", element: <Lazy><Changelog /></Lazy> },
  { path: "changelog/:slug", element: <Lazy><ChangelogPost /></Lazy> },
  { path: "sign-in", element: <Lazy><SignIn /></Lazy> },
  { path: "login", element: <LegacySignInRedirect /> },
  { path: "2fa-setup", element: <Lazy><TwoFactorBootstrap /></Lazy> },
  { path: "sign-up-shop", element: <Lazy><SignUpShop /></Lazy> },
  { path: "sign-up-team", element: <Lazy><SignUpTeam /></Lazy> },
  { path: "sign-up-team/finalize", element: <Lazy><SignUpTeamFinalize /></Lazy> },
  { path: "orgs/verify", element: <Lazy><OrgsVerify /></Lazy> },
  { path: "password-reset", element: <Lazy><PasswordResetRequest /></Lazy> },
  { path: "password-reset/:token", element: <Lazy><PasswordResetConfirm /></Lazy> },
  // KAN-169 — where the account-link e-mail lands. Mounted on BOTH
  // shells: the mail points at the tenant's own host when there is a
  // tenant apex, and falls back to the platform host when there is not.
  { path: "sso/link/:token", element: <Lazy><SsoLinkConfirm /></Lazy> },
  { path: "invitations/accept", element: <Lazy><InvitationsAccept /></Lazy> },
  // 🚨 Mounted here on purpose, and it is the ONLY reason four cross-host
  // links could stop pointing at the demo tenant (KAN-81).
  //
  // The page is user-scoped end to end — whoami, profile, password, 2FA,
  // sessions, export, delete — so it needs no tenant context to render. But
  // it was tenant-shell-only, which left the marketing shell with nowhere to
  // send a signed-in user for "my account". Every caller therefore reached
  // for ``buildTenantUrl("/account")``, and that helper with no slug falls
  // back to the DEFAULT tenant: people landed on ``demo.<apex>`` looking at
  // their own account dressed in a stranger's shop branding. It could not be
  // fixed with a session handover either — ``/auth/switch-org`` mints only
  // against a membership, and these users are not members of ``demo``.
  //
  // The page hides its tenant-scoped tiles (orders, projects, addresses)
  // when it renders here; see ``Account.tsx``.
  { path: "account", element: <Lazy><Account /></Lazy> },
  // The verify link is built from settings.frontend_url — the apex —
  // so it lands here, not on the tenant shell. Mounted on both.
  { path: "account/email-change/:token", element: <Lazy><AccountEmailChangeConfirm /></Lazy> },
  // KAN-335 — landing for the address-verification link mailed by
  // POST /auth/sign-up. Same both-shells reasoning as the line above.
  { path: "auth/verify-email/:token", element: <Lazy><AuthVerifyEmail /></Lazy> },
  // Vydavatelská plocha appek — routa MUSÍ být v obou shellech, protože
  // vývojář třetí strany se přihlašuje na platformní doméně, kdežto
  // vydavatel z řad tenantů na té svojí.
  { path: "account/developer-apps", element: <Lazy><DeveloperApps /></Lazy> },
  // Slug odpovídá ``legal_document_kinds.public_slug`` ve scope
  // ``platform`` — ``/terms`` je tady SaaS smlouva, ne e-shopové
  // podmínky, takže odkaz ze signupu konečně míří na správný dokument.
  { path: "privacy", element: <Lazy><LegalPage pageKey="privacy" /></Lazy> },
  { path: "terms", element: <Lazy><LegalPage pageKey="terms" /></Lazy> },
  { path: "cookies", element: <Lazy><LegalPage pageKey="cookies" /></Lazy> },
  { path: "gdpr", element: <Lazy><LegalPage pageKey="gdpr" /></Lazy> },
  // Jen na platformním shellu: DPA je smlouva mezi platformou a
  // tenantem, ve scope ``tenant`` by se neresolvovala. Odkazuje sem
  // checkbox souhlasu v registraci shopu — bez veřejné adresy by se
  // podepisovalo něco, co si zájemce nemohl přečíst.
  { path: "dpa", element: <Lazy><LegalPage pageKey="dpa" /></Lazy> },
  { path: "legal/subprocessors", element: <Lazy><Subprocessors /></Lazy> },
  // KAN-1243 — zveřejněná DPA (čl. 7.2 a Příloha 3) odkazuje na seznam
  // zpracovatelů cestou ``/subprocessors`` a prohlašuje ho za nedílnou
  // součást smlouvy. Tahle cesta proto nesmí skončit na 404 — vede na
  // tutéž stránku, žádná druhá kopie seznamu nevzniká.
  { path: "subprocessors", element: <Navigate to="../legal/subprocessors" relative="path" replace /> },
  { path: "*", element: <NotFound /> },
];
